import { generalCallbacks, getFormName, getFormState, storeFormState, trimFormDataFields } from './general';
import { resetCaptcha } from '../helpers/captchas';
import { TrackingHandler } from '../tracking/TrackingHandler';
import { getUrlParameter } from '../helpers/browser';
import { applyPhoneNumbersToFormData, reportPhoneValidity } from '../helpers/international-phone-inputs';
import { formatLinkNumber } from '../helpers/format-phone-number';
import { solveCaptcha } from '../forms/core/captcha';
import { buildFreeTrialPayload, ensureFreeTrialPassword, FREE_TRIAL_TYPE_REDIRECT, FREE_TRIAL_TYPE_SHOW_NUMBER, LOGIN_CUSTOMER_TYPES, LOGIN_REDIRECT_DELAY_MS, loginFreeTrialCustomer, submitFreeTrialPayload, toTrackingResultData, } from '../forms/core/free-trial-submission';
const showsNumber = (formContainer) => formContainer.querySelector('.free-trial-number') !== null;
const submitCurrentFreeTrialData = async (callbackData) => {
    const formContainer = callbackData.formContainer;
    const state = getFormState(formContainer);
    ensureFreeTrialPassword(state);
    storeFormState(formContainer, state);
    const payload = await buildFreeTrialPayload(state, callbackData.data, showsNumber(formContainer));
    payload.captcha_solution = await solveCaptcha();
    if (typeof formContainer.dataset.formName !== 'string') {
        delete payload.form_name_c;
    }
    return submitFreeTrialPayload(formContainer.dataset.endpoint || window.endpoints.freeTrial, payload, formContainer.dataset.origin || null);
};
const navigateFreeTrialCallback = (callbackData) => {
    generalCallbacks.navigateFormCallback(callbackData);
    trimFormDataFields(callbackData.data);
    if (!callbackData.data['account_name'] || !callbackData.data['mobile_number_c']) {
        return true;
    }
    if (!callbackData.data['email1'] && !callbackData.data['first_name']) {
        return true;
    }
    if (!callbackData.data['name'] && !callbackData.data['first_name']) {
        return true;
    }
    if (!applyPhoneNumbersToFormData(callbackData.formContainer, callbackData.data)) {
        reportPhoneValidity(callbackData.formContainer);
        return false;
    }
    return handleStep2Submission(callbackData);
};
const handleStep2Submission = (callbackData) => {
    const submitButton = callbackData.currentStep.querySelector('input[type="submit"], button[type="submit"]');
    if (submitButton) {
        submitButton.setAttribute('disabled', 'disabled');
    }
    return submitUnverifiedRequest(callbackData);
};
const submitUnverifiedRequest = async (callbackData) => {
    const submitErrorContainer = callbackData.currentStep.querySelector('.submit-error-message');
    const captchaContainer = callbackData.formContainer.querySelector('.captcha-container');
    submitErrorContainer?.classList?.toggle('js-hidden', true);
    callbackData.currentStep.classList.toggle('submitting', true);
    const trackingHandler = TrackingHandler.getInstance();
    const formName = getFormName(callbackData.formContainer);
    trackingHandler.formSubmit(formName, callbackData.data);
    const response = await submitCurrentFreeTrialData(callbackData);
    trackingHandler.formSubmitResult(formName, callbackData.data, response.statusCode, toTrackingResultData(response));
    callbackData.currentStep.classList.toggle('submitting', false);
    if (typeof response !== 'object') {
        submitErrorContainer?.classList?.toggle('js-hidden', false);
        return false;
    }
    if (response.data?.verification_hash) {
        callbackData.formContainer.dataset.verification_hash = response.data.verification_hash;
        return true;
    }
    if (response.result === 'conflict') {
        callbackData.currentStep.classList.toggle('js-hidden', true);
        callbackData.formContainer.querySelector('.form-step:last-of-type')?.classList?.toggle('js-hidden', false);
        processFreeTrialCreateConflict(callbackData.formContainer);
        return false;
    }
    if (response.result !== 'success') {
        if (captchaContainer) {
            resetCaptcha(captchaContainer);
        }
        const submitButton = callbackData.currentStep.querySelector('input[type="submit"], button[type="submit"]');
        if (submitButton) {
            submitButton.disabled = false;
        }
        if (submitErrorContainer) {
            submitErrorContainer.innerText = response.message;
            submitErrorContainer.classList.toggle('js-hidden', false);
        }
        return false;
    }
    callbackData.currentStep.classList.toggle('js-hidden', true);
    callbackData.formContainer.querySelector('.form-step:last-of-type')?.classList?.toggle('js-hidden', false);
    processFreeTrialSuccess(callbackData.formContainer, callbackData.data, response);
    return false;
};
const submitFreeTrialModifierCallback = async (callbackData) => {
    const data = await generalCallbacks.submitModifierCallback(callbackData);
    if (!data) {
        return;
    }
    if (callbackData.formContainer.dataset.password) {
        data.password = callbackData.formContainer.dataset.password;
    }
    if (callbackData.formContainer.dataset.verification_hash) {
        data.verification_hash = callbackData.formContainer.dataset.verification_hash;
    }
    data.free_trial_type_c = showsNumber(callbackData.formContainer)
        ? FREE_TRIAL_TYPE_SHOW_NUMBER
        : FREE_TRIAL_TYPE_REDIRECT;
    return data;
};
const submitFreeTrialResultCallback = (callbackData) => {
    if (callbackData.xhr.status === 409) {
        processFreeTrialCreateConflict(callbackData.formContainer);
        return true;
    }
    if (callbackData.xhr.status < 200 || callbackData.xhr.status > 299) {
        return true;
    }
    processFreeTrialSuccess(callbackData.formContainer, callbackData.data, callbackData.xhr.response);
    return true;
};
const toggleResponseContainer = (formContainer, selector, responseCustomerType = undefined, ...expectedCustomerTypes) => {
    const responseContainer = formContainer.querySelector(selector);
    if (!responseContainer) {
        return;
    }
    if (!responseCustomerType) {
        responseContainer.style.display = 'none';
        return;
    }
    responseContainer.style.display = expectedCustomerTypes.includes(responseCustomerType) ? 'block' : 'none';
};
const processFreeTrialCreateConflict = (formContainer) => {
    const createErrorMessage = formContainer.querySelector('.show-on-create-error');
    if (!createErrorMessage) {
        return;
    }
    formContainer.querySelectorAll('.free-trial-result-container').forEach((messageContainer) => (messageContainer.style.display = 'none'));
    createErrorMessage.style.display = 'block';
};
const processFreeTrialSuccess = (formContainer, submittedData, response) => {
    const freeTrialNumberBlocks = formContainer.querySelectorAll('.free-trial-number');
    if (freeTrialNumberBlocks.length > 0 && typeof response?.data?.displayNumber === 'string') {
        freeTrialNumberBlocks.forEach((freeTrialNumberBox) => (freeTrialNumberBox.innerText = response.data.displayNumber));
    }
    const freeTrialNumberLinks = formContainer.querySelectorAll('a.free-trial-number-link');
    if (freeTrialNumberLinks.length > 0 && typeof response?.data?.number === 'string') {
        freeTrialNumberLinks.forEach((freeTrialNumberLink) => (freeTrialNumberLink.href = 'tel:' + formatLinkNumber(response.data.number)));
    }
    const customerType = response?.data?.customerType;
    toggleResponseContainer(formContainer, '.show-on-create-error', customerType, 'NONE');
    toggleResponseContainer(formContainer, '.show-on-success', customerType, 'FREE_TRIAL');
    toggleResponseContainer(formContainer, '.show-on-exists', customerType, 'ACTIVE_FREE_TRIAL');
    toggleResponseContainer(formContainer, '.show-on-reactivated', customerType, 'REACTIVATED_FREE_TRIAL');
    toggleResponseContainer(formContainer, '.show-on-expired', customerType, 'INACTIVE');
    const username = submittedData.email1 || submittedData.first_name;
    const password = response.data?.password || formContainer.dataset.password;
    const login = () => loginFreeTrialCustomer(username, password, formContainer.dataset.loginDomain || null, formContainer.dataset.origin || null);
    const loginLinks = formContainer.querySelectorAll('.login');
    loginLinks.forEach((loginLink) => loginLink.addEventListener('click', login));
    if (freeTrialNumberBlocks.length === 0 && customerType && LOGIN_CUSTOMER_TYPES.includes(customerType)) {
        if (getUrlParameter('disable-login-redirect')) {
            return;
        }
        setTimeout(login, LOGIN_REDIRECT_DELAY_MS);
    }
};
export const freeTrialCallbacks = {
    navigateFreeTrialCallback,
    submitFreeTrialModifierCallback,
    submitFreeTrialResultCallback,
};
